/**
 * Structural half of profile validation: unknown JSON in, typed `Profile` out — or a
 * list of problems naming what is missing or the wrong shape. Semantic checks (does the
 * door's room exist, is the spawn inside a room) live in `validate.ts`, which runs on
 * the typed result of this file.
 */

import { DEFAULT_HEDGE_HEIGHT, DEFAULT_MOAT_WIDTH } from '../maze/buildGrounds.ts';
import { OBJECT_KINDS } from '../objectKinds.ts';
import type {
  Box,
  DoorSpec,
  GroundPoint,
  GroundsSpec,
  LevelSpec,
  ObjectKind,
  ObjectSpec,
  ObjectiveSpec,
  Palette,
  Profile,
  Rect,
  RoomLightSpec,
  RoomPalette,
  RoomSpec,
  Vec3,
  WallSide,
  WindowSpec,
} from './types.ts';

export type ParseResult = { profile: Profile | null; problems: string[] };

/** Derived from the kinds table, so a new kind is parseable the moment it is described. */
const OBJECT_KIND_NAMES = Object.keys(OBJECT_KINDS) as ObjectKind[];
const OBJECTIVE_KINDS = ['deposit-objects', 'reach-exit'] as const;
/** The three a profile may author. `'hedge'` is derived for maze grounds, never written. */
const FLOOR_PATTERNS = ['wood', 'tile', 'concrete'] as const;
const WALL_PATTERNS = ['wallpaper', 'paint'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Collects problems as it reads; every reader returns a safe fallback on failure. */
class Reader {
  readonly problems: string[] = [];

  string(owner: Record<string, unknown>, key: string, path: string): string {
    const value = owner[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
    this.problems.push(`${path}.${key} must be a non-empty string`);
    return '';
  }

  number(owner: Record<string, unknown>, key: string, path: string): number {
    const value = owner[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    this.problems.push(`${path}.${key} must be a finite number`);
    return 0;
  }

  record(owner: Record<string, unknown>, key: string, path: string): Record<string, unknown> {
    const value = owner[key];
    if (isRecord(value)) return value;
    this.problems.push(`${path}.${key} must be an object`);
    return {};
  }

  array(owner: Record<string, unknown>, key: string, path: string): unknown[] {
    const value = owner[key];
    if (Array.isArray(value)) return value;
    this.problems.push(`${path}.${key} must be an array`);
    return [];
  }

  groundPoint(owner: Record<string, unknown>, key: string, path: string): GroundPoint {
    const record = this.record(owner, key, path);
    return {
      x: this.number(record, 'x', `${path}.${key}`),
      z: this.number(record, 'z', `${path}.${key}`),
    };
  }

  vec3(owner: Record<string, unknown>, key: string, path: string): Vec3 {
    const record = this.record(owner, key, path);
    return {
      x: this.number(record, 'x', `${path}.${key}`),
      y: this.number(record, 'y', `${path}.${key}`),
      z: this.number(record, 'z', `${path}.${key}`),
    };
  }

  rect(owner: Record<string, unknown>, key: string, path: string): Rect {
    const record = this.record(owner, key, path);
    const at = `${path}.${key}`;
    return {
      x: this.number(record, 'x', at),
      z: this.number(record, 'z', at),
      width: this.number(record, 'width', at),
      depth: this.number(record, 'depth', at),
    };
  }

  oneOf<T extends string>(
    owner: Record<string, unknown>,
    key: string,
    allowed: readonly T[],
    path: string,
  ): T {
    const value = owner[key];
    if (typeof value === 'string' && (allowed as readonly string[]).includes(value)) {
      return value as T;
    }
    this.problems.push(
      `${path}.${key} has unknown value "${String(value)}" — expected one of: ${allowed.join(', ')}`,
    );
    return allowed[0] as T;
  }
}

function parsePatterns(
  reader: Reader,
  record: Record<string, unknown>,
  path: string,
): Pick<RoomPalette, 'floorPattern' | 'wallPattern'> {
  const patterns: Pick<RoomPalette, 'floorPattern' | 'wallPattern'> = {};
  if (record.floorPattern !== undefined) {
    patterns.floorPattern = reader.oneOf(record, 'floorPattern', FLOOR_PATTERNS, path);
  }
  if (record.wallPattern !== undefined) {
    patterns.wallPattern = reader.oneOf(record, 'wallPattern', WALL_PATTERNS, path);
  }
  return patterns;
}

function parseRoomPalette(reader: Reader, record: Record<string, unknown>, path: string): RoomPalette {
  return {
    floor: reader.string(record, 'floor', path),
    walls: reader.string(record, 'walls', path),
    ceiling: reader.string(record, 'ceiling', path),
    ...parsePatterns(reader, record, path),
  };
}

function parsePalette(reader: Reader, owner: Record<string, unknown>, path: string): Palette {
  const record = reader.record(owner, 'palette', path);
  const palette: Palette = {
    room: parseRoomPalette(reader, reader.record(record, 'room', `${path}.palette`), `${path}.palette.room`),
    door: reader.string(record, 'door', `${path}.palette`),
    box: reader.string(record, 'box', `${path}.palette`),
    zone: reader.string(record, 'zone', `${path}.palette`),
  };
  if (record.trim !== undefined) palette.trim = reader.string(record, 'trim', `${path}.palette`);
  return palette;
}

function parseRoom(reader: Reader, value: unknown, path: string): RoomSpec {
  if (!isRecord(value)) {
    reader.problems.push(`${path} must be an object`);
    return { id: '', rect: { x: 0, z: 0, width: 0, depth: 0 } };
  }

  const room: RoomSpec = {
    id: reader.string(value, 'id', path),
    rect: reader.rect(value, 'rect', path),
  };

  if (value.palette !== undefined) {
    const palette = reader.record(value, 'palette', path);
    const at = `${path}.palette`;
    const partial: Partial<RoomPalette> = { ...parsePatterns(reader, palette, at) };
    if (palette.floor !== undefined) partial.floor = reader.string(palette, 'floor', at);
    if (palette.walls !== undefined) partial.walls = reader.string(palette, 'walls', at);
    if (palette.ceiling !== undefined) partial.ceiling = reader.string(palette, 'ceiling', at);
    room.palette = partial;
  }

  if (value.light !== undefined) {
    room.light = parseRoomLight(reader, reader.record(value, 'light', path), `${path}.light`);
  }

  if (value.lightSwitch !== undefined) {
    const record = reader.record(value, 'lightSwitch', path);
    const at = `${path}.lightSwitch`;
    const wall = reader.string(record, 'wall', at);
    if (!WALL_SIDES.includes(wall as WallSide)) {
      reader.problems.push(`${at}.wall must be one of ${WALL_SIDES.join(', ')}`);
    }
    room.lightSwitch = {
      wall: (WALL_SIDES.includes(wall as WallSide) ? wall : 'north') as WallSide,
      offset: reader.number(record, 'offset', at),
    };
  }

  if (value.windows !== undefined) {
    room.windows = reader
      .array(value, 'windows', path)
      .map((window, index) => parseWindow(reader, window, `${path}.windows[${index}]`));
  }

  return room;
}

const WALL_SIDES: readonly WallSide[] = ['west', 'east', 'north', 'south'];

function parseWindow(reader: Reader, value: unknown, path: string): WindowSpec {
  if (!isRecord(value)) {
    reader.problems.push(`${path} must be an object`);
    return { id: '', wall: 'north', offset: 0, width: 0, sill: 0, head: 0 };
  }

  const wall = reader.string(value, 'wall', path);
  if (!WALL_SIDES.includes(wall as WallSide)) {
    reader.problems.push(`${path}.wall must be one of ${WALL_SIDES.join(', ')}`);
  }

  return {
    id: reader.string(value, 'id', path),
    wall: (WALL_SIDES.includes(wall as WallSide) ? wall : 'north') as WallSide,
    offset: reader.number(value, 'offset', path),
    width: reader.number(value, 'width', path),
    sill: reader.number(value, 'sill', path),
    head: reader.number(value, 'head', path),
  };
}

function parseRoomLight(
  reader: Reader,
  record: Record<string, unknown>,
  path: string,
): RoomLightSpec {
  const light: RoomLightSpec = {};
  if (record.color !== undefined) light.color = reader.string(record, 'color', path);
  if (record.intensity !== undefined) light.intensity = reader.number(record, 'intensity', path);
  if (record.drop !== undefined) light.drop = reader.number(record, 'drop', path);
  if (record.offset !== undefined) {
    const offset = reader.record(record, 'offset', path);
    light.offset = {
      x: reader.number(offset, 'x', `${path}.offset`),
      z: reader.number(offset, 'z', `${path}.offset`),
    };
  }
  return light;
}

function parseDoor(reader: Reader, value: unknown, path: string): DoorSpec {
  if (!isRecord(value)) {
    reader.problems.push(`${path} must be an object`);
    return { id: '', betweenRooms: ['', ''], center: { x: 0, z: 0 }, width: 0, height: 0 };
  }

  const rooms = reader.array(value, 'betweenRooms', path);
  if (rooms.length !== 2 || rooms.some((room) => typeof room !== 'string')) {
    reader.problems.push(`${path}.betweenRooms must be a pair of room ids`);
  }
  const [roomA, roomB] = rooms;

  const door: DoorSpec = {
    id: reader.string(value, 'id', path),
    betweenRooms: [typeof roomA === 'string' ? roomA : '', typeof roomB === 'string' ? roomB : ''],
    center: reader.groundPoint(value, 'center', path),
    width: reader.number(value, 'width', path),
    height: reader.number(value, 'height', path),
  };

  if (value.isInitiallyOpen === undefined) return door;
  if (typeof value.isInitiallyOpen !== 'boolean') {
    reader.problems.push(`${path}.isInitiallyOpen must be a boolean`);
    return door;
  }
  return { ...door, isInitiallyOpen: value.isInitiallyOpen };
}

function parseObject(reader: Reader, value: unknown, path: string): ObjectSpec {
  if (!isRecord(value)) {
    reader.problems.push(`${path} must be an object`);
    return { id: '', kind: 'cardboard-box', roomId: '', position: { x: 0, z: 0 } };
  }

  const object: ObjectSpec = {
    id: reader.string(value, 'id', path),
    kind: reader.oneOf(value, 'kind', OBJECT_KIND_NAMES, path),
    roomId: reader.string(value, 'roomId', path),
    position: reader.groundPoint(value, 'position', path),
  };

  if (value.size === undefined) return object;
  return { ...object, size: reader.vec3(value, 'size', path) };
}

function parseObjective(reader: Reader, value: unknown, path: string): ObjectiveSpec {
  if (!isRecord(value)) {
    reader.problems.push(`${path} must be an object`);
    return {
      id: '',
      kind: 'deposit-objects',
      description: '',
      objectIds: [],
      zone: { roomId: '', rect: { x: 0, z: 0, width: 0, depth: 0 } },
    };
  }

  const kind = reader.oneOf(value, 'kind', OBJECTIVE_KINDS, path);
  const id = reader.string(value, 'id', path);
  const description = reader.string(value, 'description', path);

  // Reaching the exit has nothing to author: the zone is the gap the maze generator
  // opened, and the objects are the player.
  if (kind === 'reach-exit') return { id, kind, description };

  const ids = reader.array(value, 'objectIds', path);
  if (ids.some((objectId) => typeof objectId !== 'string')) {
    reader.problems.push(`${path}.objectIds must contain only object ids`);
  }
  const zone = reader.record(value, 'zone', path);

  return {
    id,
    kind,
    description,
    objectIds: ids.filter((objectId): objectId is string => typeof objectId === 'string'),
    zone: {
      roomId: reader.string(zone, 'roomId', `${path}.zone`),
      rect: reader.rect(zone, 'rect', `${path}.zone`),
    },
  };
}

/**
 * The optional grounds block. Both defaults are filled in here rather than left to the
 * geometry, so validation and rendering see the same numbers a profile would have had to
 * write out by hand.
 */
function parseGrounds(reader: Reader, owner: Record<string, unknown>, path: string): GroundsSpec {
  const record = reader.record(owner, 'grounds', path);
  const at = `${path}.grounds`;
  if (record.startCourtyard !== undefined && typeof record.startCourtyard !== 'boolean') {
    reader.problems.push(`${at}.startCourtyard must be a boolean`);
  }
  return {
    startCourtyard: record.startCourtyard === true,
    seed: reader.string(record, 'seed', at),
    columns: reader.number(record, 'columns', at),
    rows: reader.number(record, 'rows', at),
    hedgeHeight:
      record.hedgeHeight === undefined
        ? DEFAULT_HEDGE_HEIGHT
        : reader.number(record, 'hedgeHeight', at),
    moatWidth:
      record.moatWidth === undefined ? DEFAULT_MOAT_WIDTH : reader.number(record, 'moatWidth', at),
    ladderCount:
      record.ladderCount === undefined ? 0 : reader.number(record, 'ladderCount', at),
    tableCount: record.tableCount === undefined ? 0 : reader.number(record, 'tableCount', at),
    tableItems:
      record.tableItems === undefined
        ? []
        : reader
            .array(record, 'tableItems', at)
            .map((_, index) =>
              reader.oneOf(
                record.tableItems as Record<string, unknown>,
                String(index),
                OBJECT_KIND_NAMES,
                `${at}.tableItems`,
              ),
            ),
  };
}

function parseStep(reader: Reader, value: unknown, path: string): Box {
  if (!isRecord(value)) {
    reader.problems.push(`${path} must be an object`);
    return { center: { x: 0, y: 0, z: 0 }, size: { x: 0, y: 0, z: 0 } };
  }
  return {
    center: reader.vec3(value, 'center', path),
    size: reader.vec3(value, 'size', path),
  };
}

function parseLevel(reader: Reader, value: unknown, path: string): LevelSpec {
  if (!isRecord(value)) {
    reader.problems.push(`${path} must be an object`);
    return { id: '', spawn: { position: { x: 0, z: 0 }, yaw: 0 }, rooms: [], doors: [], objects: [], objectives: [] };
  }

  const spawn = reader.record(value, 'spawn', path);
  const level: LevelSpec = {
    id: reader.string(value, 'id', path),
    spawn: {
      position: reader.groundPoint(spawn, 'position', `${path}.spawn`),
      yaw: reader.number(spawn, 'yaw', `${path}.spawn`),
    },
    rooms: reader.array(value, 'rooms', path).map((room, i) => parseRoom(reader, room, `${path}.rooms[${i}]`)),
    doors: reader.array(value, 'doors', path).map((door, i) => parseDoor(reader, door, `${path}.doors[${i}]`)),
    objects: reader
      .array(value, 'objects', path)
      .map((object, i) => parseObject(reader, object, `${path}.objects[${i}]`)),
    objectives: reader
      .array(value, 'objectives', path)
      .map((objective, i) => parseObjective(reader, objective, `${path}.objectives[${i}]`)),
  };

  const extras: {
    ceilingHeight?: number;
    steps?: readonly Box[];
    grounds?: GroundsSpec;
    exitsTo?: string;
  } = {};
  if (value.ceilingHeight !== undefined) {
    extras.ceilingHeight = reader.number(value, 'ceilingHeight', path);
  }
  if (value.steps !== undefined) {
    extras.steps = reader
      .array(value, 'steps', path)
      .map((step, i) => parseStep(reader, step, `${path}.steps[${i}]`));
  }
  if (value.grounds !== undefined) {
    extras.grounds = parseGrounds(reader, value, path);
  }
  if (value.exitsTo !== undefined) {
    extras.exitsTo = reader.string(value, 'exitsTo', path);
  }

  return { ...level, ...extras };
}

export function parseProfileData(data: unknown): ParseResult {
  if (!isRecord(data)) {
    return { profile: null, problems: ['profile must be a JSON object'] };
  }

  const reader = new Reader();
  const profile: Profile = {
    id: reader.string(data, 'id', 'profile'),
    name: reader.string(data, 'name', 'profile'),
    palette: parsePalette(reader, data, 'profile'),
    levels: reader.array(data, 'levels', 'profile').map((level, i) => parseLevel(reader, level, `levels[${i}]`)),
  };

  if (profile.levels.length === 0) {
    reader.problems.push('profile.levels must contain at least one level');
  }

  if (data.shortName !== undefined) {
    profile.shortName = reader.string(data, 'shortName', 'profile');
  }

  // Read the way a door reads `isInitiallyOpen`: absent is the default and says nothing,
  // present and wrong is a problem rather than a silently ignored key.
  if (data.pocket !== undefined) {
    if (typeof data.pocket === 'boolean') profile.pocket = data.pocket;
    else reader.problems.push('profile.pocket must be a boolean');
  }

  if (data.endless !== undefined) {
    if (isRecord(data.endless)) {
      const path = 'profile.endless';
      profile.endless = {
        seed: reader.string(data.endless, 'seed', path),
        minCells: reader.number(data.endless, 'minCells', path),
        maxCells: reader.number(data.endless, 'maxCells', path),
        objective: reader.string(data.endless, 'objective', path),
      };
    } else reader.problems.push('profile.endless must be an object');
  }

  return reader.problems.length > 0
    ? { profile: null, problems: reader.problems }
    : { profile, problems: [] };
}
